import { Close, Info, MoreVert } from '@material-ui/icons'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Box,
  Button,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useSelector } from 'react-redux'
import { PromoCodeMethod, modifyPromoCode } from '../../modules/promoCodeModify'
import { fetchPromoCodes } from '../../modules/promoCodes'
import { useAppDispatch } from '../../store'
import ActionsMenu from '../ActionsMenu/ActionsMenu'
import Loader from '../Loader/Loader'
import Message from '../Message/Message'
import ModalPopUp from '../ModalPopUp/ModalPopUp'
import ToastMessage from '../ToastMessage/ToastMessage'
import AddEditPromoCodes from './AddEditPromoCodes'

const PromoCodes = () => {
  const dispatch = useAppDispatch()

  const { loading, error, promoCodes } = useSelector(
    (state: RootStateOrAny) => state.promoCodes
  )

  const { method, success } = useSelector(
    (state: RootStateOrAny) => state.promoCodeCreate
  )

  const [showAddEditModal, setShowAddEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [promoCode, setPromoCode] = useState<PromoCode | undefined>(undefined)
  const [successToast, setSuccessToast] = useState(false)

  useEffect(() => {
    dispatch(fetchPromoCodes())

    if (success) {
      setShowAddEditModal(false)
      setSuccessToast(true)
      setShowDeleteModal(false)
      setPromoCode(undefined)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, success])

  const handleAddNewClick = () => {
    setShowAddEditModal(true)
  }

  const handleEditClick = (code: PromoCode) => {
    setPromoCode(code)
    setShowAddEditModal(true)
  }

  const handleDeleteClick = (code: PromoCode) => {
    setPromoCode(code)
    setShowDeleteModal(true)
  }

  const handleCancelDelete = () => {
    setPromoCode(undefined)
    setShowDeleteModal(false)
  }

  const handleDelete = () => {
    if (!promoCode) return
    dispatch(modifyPromoCode(promoCode, PromoCodeMethod.DELETE))
  }

  return (
    <>
      <div className="library gray-bg">
        <Container className="p-b-100">
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 className="page-title accent-left">Promo Codes</h3>

            <Button
              color="primary"
              variant="contained"
              sx={{ height: 36 }}
              onClick={handleAddNewClick}
            >
              Add New
            </Button>
          </Box>

          <div className="flex flex-end m-b-16">
            {loading && <Loader />}

            {error && <Message variant="error">{error}</Message>}

            {!loading && !error && promoCodes.length ? (
              <TableContainer>
                <Table stickyHeader aria-label="reporting table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {promoCodes.map((code: PromoCode, index: number) => (
                      <TableRow key={`promo-${index}`}>
                        <TableCell>{code.name}</TableCell>
                        <TableCell>{code.value}%</TableCell>
                        <TableCell>
                          {code.description && code.description}
                        </TableCell>
                        <TableCell>
                          {code.expires &&
                            code.start &&
                            dayjs(code.start).format('MM/DD/YYYY')}
                        </TableCell>
                        <TableCell>
                          {code.expires &&
                            code.end &&
                            dayjs(code.end).format('MM/DD/YYYY')}
                        </TableCell>
                        <TableCell>
                          {code.active ? (
                            <CheckCircleOutlineIcon
                              sx={{ color: 'var(--color-success)' }}
                            />
                          ) : (
                            <Close style={{ color: 'var( --color-gray)' }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <ActionsMenu
                            icon={<MoreVert />}
                            options={[
                              {
                                onClick: () => handleEditClick(code),
                                label: 'Edit/View',
                              },

                              {
                                onClick: () => handleDeleteClick(code),
                                label: 'Delete',
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </div>
          {!loading && !error && !promoCodes.length && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              <Info
                className="m-b-35"
                style={{ fontSize: 42, color: '#cacaca' }}
              />
              <h5 className="gray-text fs18 m-b-32">No promo codes</h5>
            </Box>
          )}
        </Container>
      </div>

      {showAddEditModal && (
        <AddEditPromoCodes
          showModal={showAddEditModal}
          setShowModal={setShowAddEditModal}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
        />
      )}

      <ModalPopUp
        title="Delete promo code"
        setShowModal={handleCancelDelete}
        showModal={showDeleteModal}
        variant="small"
      >
        <h4 className="m-b-30">Delete promo code</h4>

        <p>
          Are you sure you want to delete <strong>{promoCode?.name}?</strong>
        </p>

        {error && <Message variant="error">{error}</Message>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
          <Button
            color="primary"
            variant="outlined"
            className="m-r-24"
            onClick={handleCancelDelete}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </Box>
      </ModalPopUp>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">
              Promo code{' '}
              {method === PromoCodeMethod.CREATE
                ? 'created'
                : method === PromoCodeMethod.UPDATE
                ? 'updated'
                : 'deleted'}
            </p>
            <p className="fs14 m-b-0">
              You've successfully{' '}
              {method === PromoCodeMethod.CREATE
                ? 'created'
                : method === PromoCodeMethod.UPDATE
                ? 'updated'
                : 'deleted'}{' '}
              the promo code.
            </p>
          </>
        }
        open={successToast}
        setOpen={setSuccessToast}
      />
    </>
  )
}

export default PromoCodes
